import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Pathname } from 'Routes';
import { Button } from 'Components';
import { AppContext, AuthContext } from 'Context';
import { isMobile } from 'react-device-detect';
import { axios, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { encryptedKeyForCredentials } from 'Commons';
import { AuthPage } from '../components/page';
import { getDesktopCallbackUrl } from '../utils/desktopCallback';
import VerificationLoader from '../../../../Components/VerificationLoader';
import styles from './index.module.css';

function VerifyUsingLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setAppSnackbar, userDetails, setPlansPage } = useContext(AppContext);
  const { forceAutoSignin, setToken, setPreferredEmail } = useContext(AuthContext);
  const [verificationMessage, setVerificationMessage] = useState({ type: 'waiting', message: 'Waiting for verification...' });
  // Get userId from URL params or userDetails
  const userId = searchParams.get('userId') || userDetails?.id;
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Check for desktop app
  const appType = searchParams.get('app');
  const redirectUri = searchParams.get('redirect_uri');
  const isDesktopApp = appType === 'desktop' || appType === 'windows-desktop';

  // Save uuid and code in state
  const [uuid, setUuid] = useState(searchParams.get('uuid'));
  const [code, setCode] = useState(searchParams.get('code'));
  const [from, setFrom] = useState(searchParams.get('from'));
  const [verificationData, setVerificationData] = useState(null);

  useEffect(() => {
    const fromValue = searchParams.get('from');
    setFrom(fromValue);
  }, []);

  // Automatically call verification API when component mounts
  useEffect(() => {
    const verifyEmailAutomatically = async () => {
      if (uuid && code) {
        try {
          setIsLoading(true);
          setVerificationMessage({ type: 'waiting', message: 'Verifying your email...' });

          const response = await axios.post(endpoints.verifyEmailToLogin, {
            uuid,
            code
          });

          // Store verification data for later use
          setVerificationData(response.data);
          setIsVerified(true);
          setVerificationMessage({ type: 'success', message: 'Verified Successfully!' });

          // Show success message
          setAppSnackbar({
            open: true,
            message: 'Email verified successfully!',
            severity: 'success'
          });
        } catch (error) {
          logAppEvent({
            userName: 'Guest',
            functionAffected: 'Email Verification via Link',
            description: `Email verification failed — uuid: ${uuid || 'unknown'}, error: ${error?.response?.data?.message || error?.message || 'Unknown error'}`,
            severity: 'high',
            errorMessage: error?.response?.data?.message || error?.message || 'Email verification failed',
            errorStack: error?.stack || null,
            responseStatus: error?.response?.status || null,
            responseBody: JSON.stringify(error?.response?.data) || null,
            requestUrl: endpoints.verifyEmailToLogin,
            requestMethod: 'POST',
            component: 'VerifyUsingLink',
            userAction: 'email verification via link click',
          });
          setVerificationMessage({
            type: 'error',
            message: error?.response?.data?.message || 'Email verification failed. Please try again.'
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    verifyEmailAutomatically();
  }, [uuid, code, setAppSnackbar]);

  // Handle navigation after button click
  const handleProceedToApp = () => {
    if (verificationData?.new_user) {
      setPreferredEmail(verificationData?.email);
      sessionStorage.setItem(encryptedKeyForCredentials.email, verificationData?.email);
      setPlansPage(false);
      setToken(verificationData?.data?.token);
      forceAutoSignin();

      // Handle desktop app callback for new users
      if (isDesktopApp) {
        const callbackParams = {
          token: verificationData?.api_response?.access || '',
          projector_token: verificationData.data?.token || '',
          email: verificationData?.email || '',
          isNewUser: verificationData.new_user
        };
        window.location.href = getDesktopCallbackUrl(callbackParams);
        return;
      }

      navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true } });
      localStorage.removeItem('loginEmailNewUser');
    }
  };

  return (
    <div>
      <AuthPage>
        <VerificationLoader
          from={from}
          isVerified={isVerified}
          verificationMessage={verificationMessage}
          verifyEmail={handleProceedToApp}
          isLoading={isLoading}
        />
      </AuthPage>
    </div>
  );
}

export default VerifyUsingLink;
